import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Stack, Text, Button, Spinner, Box } from '@nike/podium';
import { CaretDown } from '@nike/nike-design-system-icons';
import { useAtomValue, useSetAtom } from 'jotai';
import { projectsDataAtom, expressionsDataAtom } from '~/atoms/gridData';
import { detailSidebarAtom } from '~/atoms/pageActions';
import { fetchModelById } from '~/service/fetchModelsData';
import ProjectDetailContent from './ProjectDetailContent';
import ExpressionDetailContent from './ExpressionDetailContent';
import ModelDetailContent from './ModelDetailContent';
import ModelDetailHeader from './ModelDetailHeader';
export { ProjectDetailContent, ExpressionDetailContent, ModelDetailContent, ModelDetailHeader };
export { TeamMemberCard } from './TeamMemberCard';
export { TeamMemberList } from './TeamMemberList';
const linePlanEntityMap = {
    projects: 'project',
    expressions: 'expression',
    models: 'model'
};
const entityConfigs = {
    project: {
        entityType: 'project',
        entityLabel: 'Project',
        backUrl: '/projects/view/card',
        backLabel: 'Back to Projects'
    },
    expression: {
        entityType: 'expression',
        entityLabel: 'Expression',
        backUrl: '/expressions/view/card',
        backLabel: 'Back to Expressions'
    },
    model: {
        entityType: 'model',
        entityLabel: 'Model',
        backUrl: '/models/view/card',
        backLabel: 'Back to Models'
    }
};
function useEntityLoader(entityType, handle) {
    const projects = useAtomValue(projectsDataAtom);
    const expressions = useAtomValue(expressionsDataAtom);
    // For non-model entities, use atom lookup
    if (entityType !== 'model') {
        const atomData = entityType === 'project' ? projects : expressions;
        const config = entityConfigs[entityType];
        if (!atomData || atomData.length === 0) {
            return { entity: null, isLoading: true, error: null };
        }
        const entity = atomData.find((e) => e.handle === handle || e.id === handle);
        return {
            entity: entity || null,
            isLoading: false,
            error: entity ? null : `${config.entityLabel} not found`
        };
    }
    // For models, return loading state - actual fetch happens in useModelLoader
    return { entity: null, isLoading: true, error: null };
}
/**
 * Hook for fetching a single model by ID.
 * Uses Option C approach: always fetch single model from API. This is safer than relying on atom state as the models data for that might not include the individual model data. This
 * will change when we fetch this data from projects api instead of bff.
 */
function useModelLoader(modelId) {
    const [model, setModel] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!modelId) {
            setIsLoading(false);
            setError('Model ID is required');
            return;
        }
        const loadModel = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const result = await fetchModelById(modelId);
                if (result) {
                    setModel(result);
                }
                else {
                    setError('Model not found');
                }
            }
            catch {
                setError('Failed to load model');
            }
            finally {
                setIsLoading(false);
            }
        };
        loadModel();
    }, [modelId]);
    return { entity: model, isLoading, error };
}
function getEntityName(entity, entityType) {
    if (entityType === 'project') {
        return entity.projectName || 'Project';
    }
    if (entityType === 'model') {
        return entity.modelName || 'Model';
    }
    return entity.expressionName || 'Expression';
}
function EntityDetailPage() {
    const { linePlanEntity, handle } = useParams();
    const location = useLocation();
    const setDetailSidebar = useSetAtom(detailSidebarAtom);
    const projects = useAtomValue(projectsDataAtom);
    const entityType = linePlanEntityMap[linePlanEntity ?? ''] ?? 'project';
    const config = entityConfigs[entityType];
    // Use navigation state for back URL if available, otherwise use default config
    const locationState = location.state;
    const backUrl = locationState?.from ?? config.backUrl;
    const backLabel = locationState?.fromLabel ?? config.backLabel;
    // Use different loaders based on entity type
    const atomLoaderResult = useEntityLoader(entityType, handle);
    const modelLoaderResult = useModelLoader(entityType === 'model' ? handle : undefined);
    // Select the appropriate result based on entity type
    const { entity, isLoading, error } = entityType === 'model' ? modelLoaderResult : atomLoaderResult;
    const handleBackNavigation = useCallback(() => {
        // If we should return to a specific project panel
        if (locationState?.returnToProjectPanel && locationState.projectId) {
            const project = projects?.find((p) => p.id === locationState.projectId);
            if (project) {
                setDetailSidebar({
                    isOpen: true,
                    entity: project,
                    entityType: 'project'
                });
                return;
            }
        }
        if (locationState?.reopenSidebar && locationState.sidebarEntity) {
            setDetailSidebar({
                isOpen: true,
                entity: locationState.sidebarEntity,
                entityType: locationState.sidebarEntityType || config.entityType
            });
        }
        else {
            setDetailSidebar({
                isOpen: false,
                entity: null,
                entityType: config.entityType
            });
        }
    }, [setDetailSidebar, config.entityType, locationState, projects]);
    const entityName = useMemo(() => {
        if (!entity)
            return '';
        return getEntityName(entity, entityType);
    }, [entity, entityType]);
    if (isLoading) {
        return (_jsxs(Stack, { gap: "l", className: "p-6 items-center justify-center h-64", children: [_jsx(Spinner, { size: "large" }), _jsxs(Text, { font: "body-2", children: ["Loading ", config.entityLabel, "..."] })] }));
    }
    if (error || !entity) {
        return (_jsxs(Stack, { gap: "l", className: "p-6", children: [_jsxs(Text, { font: "title-3", children: [config.entityLabel, " not found"] }), _jsx(Link, { to: backUrl, onClick: handleBackNavigation, children: _jsx(Button, { variant: "outlined", size: "s", children: backLabel }) })] }));
    }
    // Model has a different layout with header card
    if (entityType === 'model') {
        const model = entity;
        return (_jsxs(Stack, { gap: "s", className: "p-6", children: [_jsxs(Link, { to: backUrl, onClick: handleBackNavigation, className: "inline-flex items-center gap-1 no-underline", children: [_jsx(CaretDown, { className: "w-4 h-4 rotate-90" }), _jsx(Text, { font: "body-2", className: "underline", children: backLabel })] }), _jsx(ModelDetailHeader, { model: model, modelId: handle }), _jsx(Box, { className: "rounded-lg bg-card px-6 py-2", children: _jsx(ModelDetailContent, { model: model }) })] }));
    }
    // Project and Expression layout
    return (_jsxs(Stack, { gap: "l", className: "p-6", children: [_jsxs(Stack, { gap: "m", children: [_jsxs(Link, { to: backUrl, onClick: handleBackNavigation, className: "inline-flex items-center gap-1", children: [_jsx(CaretDown, { className: "w-4 h-4 rotate-90" }), _jsx(Text, { font: "body-2", className: "underline", children: backLabel })] }), _jsx(Text, { font: "title-2", className: "font-black uppercase", children: entityName })] }), entityType === 'project' ? (_jsx(ProjectDetailContent, { project: entity })) : (_jsx(ExpressionDetailContent, { expression: entity }))] }));
}
export { EntityDetailPage };
